import React, { useMemo } from 'react';
import moment from 'moment';
import { useGraphData } from '../../hooks/useMeters';
import { buildWidgetConfig } from '../../services/meterApi';
import { relativeRangeWithin } from '../../utils/financialYearWindow';
import {
  waterBalance,
  kpiLabel,
  BALANCE_KPIS,
  TANKER_KPIS,
  BALANCE_KPI_LIST,
} from '../../utils/waterBalance';
import { metersBehind } from '../../utils/meterContributors';
import ContributorsPopover from './ContributorsPopover';
import { balanceRows, describeBalance, BALANCE_PANEL_ID } from '../../utils/balanceInsights';
import { useInsights } from '../../../../../globalComponents/insights/useInsights';
import InsightsPanel from '../../../../../globalComponents/insights/InsightsPanel';
import { INSIGHTS_TAB } from './waterCharts';

/**
 * Does the site's water add up?
 *
 * One request covers every component, so the balance is read off a single day
 * rather than assembled from eight cards that each chose their own most recent
 * reporting day.
 */

/**
 * Reads the rows out of the widget response, whichever shape it arrives in.
 * @param {object} payload - Response from the graph endpoint.
 * @returns {Array<object>} The rows, empty when there are none.
 */
const rowsOf = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

/**
 * Formats a component of the balance.
 * @param {number} value - Cubic metres.
 * @returns {string} The figure at the precision the cards use.
 */
const m3 = (value) => value.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * @param {string} currentFY - Financial year the balance is scoped to.
 * @param {string|number} locationId - Location filter from the page header.
 * @param {string|number} subLocationId - Sub-location filter from the page header.
 * @param {Array<object>} meters - Every meter the page holds, so each component
 *   of the balance can name what it is measured by.
 * @param {boolean} showInsights - Whether the page's insights switch is on.
 * @param {Function} onInsightsReady - Told what this panel's narrative says, so
 *   the page can summarise every figure at once.
 */
const WaterBalancePanel = ({
  currentFY,
  locationId,
  subLocationId,
  meters,
  showInsights = false,
  onInsightsReady,
}) => {
  const config = useMemo(() => {
    if (!currentFY) return null;

    const range = relativeRangeWithin('week', currentFY);

    const built = {
      financialYear: currentFY,
      startDateTime: range.start.toISOString(),
      endDateTime: range.end.toISOString(),
      locationId: locationId?.toString() || '1',
      title: 'Water balance',
      module: 'Water',
      kpi: BALANCE_KPI_LIST,
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: 'days',
      xAxisField: 'displayPeriods',
      stackByField: 'kpi',
      enableStacking: true,
    };

    if (subLocationId) built.subLocationId = subLocationId.toString();

    return buildWidgetConfig(built);
  }, [currentFY, locationId, subLocationId]);

  const { data, isLoading } = useGraphData(config, { enabled: !!config });

  const balance = useMemo(() => waterBalance(rowsOf(data)), [data]);

  const from = useMemo(() => {
    const behind = (kpi) => metersBehind({ kpi }, meters, currentFY);

    return {
      borewell: behind([BALANCE_KPIS.BOREWELL]),
      tanker: behind(TANKER_KPIS),
      consumed: behind([BALANCE_KPIS.ADMIN_TOP]),
    };
  }, [meters, currentFY]);

  /**
   * A request the panel never sends, describing what it drew.
   *
   * The narrative is written from the four totals and the verdict on them, not
   * from the KPI-by-day series they were distilled out of. It carries no KPI
   * filter, because the rows are named for the components rather than for the
   * KPIs behind them and a filter naming those KPIs would match none of them.
   */
  const insightsConfig = useMemo(() => {
    if (!config) return null;

    const range = relativeRangeWithin('week', currentFY);

    const built = {
      financialYear: currentFY,
      startDateTime: range.start.toISOString(),
      endDateTime: range.end.toISOString(),
      locationId: locationId?.toString() || '1',
      title: 'Water Balance',
      module: 'Water',
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: 'days',
      xAxisField: 'kpi',
    };

    if (subLocationId) built.subLocationId = subLocationId.toString();

    return { ...buildWidgetConfig(built), description: describeBalance(balance) };
  }, [config, currentFY, locationId, subLocationId, balance]);

  const insightsData = useMemo(() => ({ data: balanceRows(balance) }), [balance]);

  /**
   * Asked for even on a day nothing balanced, so a page-wide summary is not
   * left waiting on a figure that is never coming.
   */
  const insightsEnabled = showInsights && !!insightsConfig && !isLoading;

  const {
    insights,
    loading: insightsLoading,
    error: insightsError,
    refresh: refreshInsights,
  } = useInsights(
    INSIGHTS_TAB,
    BALANCE_PANEL_ID,
    insightsData,
    insightsConfig,
    insightsEnabled,
    onInsightsReady
  );

  if (isLoading) {
    return (
      <section className="wb-panel">
        <h3 className="section-title">Water Balance</h3>
        <div className="wb-skeleton" aria-label="Loading water balance" />
      </section>
    );
  }

  if (!balance) {
    return (
      <section className="wb-panel">
        <h3 className="section-title">Water Balance</h3>
        <div className="wb-card">
          <p className="wb-empty">
            No day in this window reported both a source and consumption, so
            there is nothing to balance against.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="wb-panel">
      <h3 className="section-title">Water Balance</h3>

      <div className="wb-card">
        <div className="wb-head">
          <h4 className="wb-title">
            {moment(balance.periodTs).format('DD MMM YYYY')}, across all sources
          </h4>
          <span className={`wb-verdict wb-verdict--${balance.balances ? 'ok' : 'bad'}`}>
            {balance.balances ? 'Balances' : 'Does not balance'}
          </span>

          {balance.consumedExceedsSource && (
            <p className="wb-why">
              Groundwater consumption exceeds groundwater withdrawal by{' '}
              {m3(balance.groundConsumed - balance.groundWithdrawn)} m³. Water cannot
              leave a site it never entered, so a meter in this chain is
              misreading or treatment is drawing on stored volume.
            </p>
          )}

          {balance.treatedShare !== null && (
            <p className={`wb-why${balance.treatedExceedsWithdrawn ? ' wb-why--bad' : ''}`}>
              {balance.treatedExceedsWithdrawn
                ? `Treated water came to ${balance.treatedShare.toFixed(1)}% of everything withdrawn — more was treated than was taken in from any source, which cannot be physically true either.`
                : `Treated water came to ${balance.treatedShare.toFixed(1)}% of everything withdrawn.`}
            </p>
          )}
        </div>

        <dl className="wb-flow">
          <div className="wb-cell">
            <dt>Borewell withdrawn</dt>
            <b>{m3(balance.groundWithdrawn)}<span>m³</span></b>
            <small>Pulled up out of the ground</small>
            <ContributorsPopover contributors={from.borewell} title="borewell withdrawn" />
          </div>

          <div className={`wb-cell${balance.consumedExceedsSource ? ' wb-cell--flag' : ''}`}>
            <dt>Borewell consumed</dt>
            <b>{m3(balance.groundConsumed)}<span>m³</span></b>
            <small>
              {balance.consumedExceedsSource
                ? `${m3(balance.groundConsumed - balance.groundWithdrawn)} above its own source`
                : 'From borewell to admin top'}
            </small>
            <ContributorsPopover contributors={from.consumed} title="borewell consumed" />
          </div>

          <div className="wb-cell">
            <dt>Tanker withdrawn</dt>
            <b>{m3(balance.tankerWithdrawn)}<span>m³</span></b>
            <small>Bought in and delivered</small>
            <ContributorsPopover contributors={from.tanker} title="tanker withdrawn" />
          </div>

          <div className="wb-cell">
            <dt>Tanker consumed</dt>
            <b>{m3(balance.tankerConsumed)}<span>m³</span></b>
            <small>Assumed equal to what was delivered — nothing meters it</small>
            <ContributorsPopover contributors={from.tanker} title="tanker consumed" />
          </div>
        </dl>

        {balance.silent.length > 0 && (
          <p className="wb-note">
            Did not report on this day: {balance.silent.map(kpiLabel).join(', ')}. Those
            Meters count as nothing in this balance, which understates the
            totals they belong to.
          </p>
        )}

        {showInsights && (
          <div className="im-insights-panel">
            <InsightsPanel
              insights={insights}
              loading={insightsLoading}
              error={insightsError}
              onRefresh={refreshInsights}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default WaterBalancePanel;
